import { ActionContext, Store } from 'vuex';
import StoreUtil from '@store/StoreUtil';
import config from '@config';
import { IState } from '@app/store';
import { TicketsLoadStatus } from '@store/modules/data/tickets/actions';
import ITicket from '@models/tickets/Ticket';

const PAGE_SIZE = config.app.ticketsPage.defaultPageSize;

export interface ITicketPageState  {
    lastFetchedActiveTicketId: null | string;
    lastFetchedFinishedTicketId: null | string;
    lastFinishedTicketId: null | string;
    lastActiveTicketId: null | string;
    ticketsBeingLoaded: null | TicketsLoadStatus;
}

const defaultState : ITicketPageState = {
    lastFetchedActiveTicketId: null, // used to store last fetched id. Last fetched id us used for pagination
    // It cannot be derived from tickets state because they're mixed with tickets from tickets stack
    // which aren't loader with the same ordering
    lastFetchedFinishedTicketId: null, // as above
    lastFinishedTicketId: null, // last shown id
    lastActiveTicketId: null, // as above
    ticketsBeingLoaded: null,
};

const SET_LAST_ACTIVE_TICKET_ID = 'SET_LAST_ACTIVE_TICKET_ID';
const SET_LAST_FINISHED_TICKET_ID = 'SET_LAST_FINISHED_TICKET_ID';
const SET_LAST_FETCHED_ACTIVE_TICKET_ID = 'SET_LAST_FETCHED_ACTIVE_TICKET_ID';
const SET_LAST_FETCHED_FINISHED_TICKET_ID = 'SET_LAST_FETCHED_FINISHED_TICKET_ID';
const SET_TICKETS_BEINGS_LOADED = 'SET_TICKETS_BEINGS_LOADED';
const CLEAR_ALL = 'CLEAR_ALL';

type actionContext = ActionContext<ITicketPageState, IState>;

export default {
    state: defaultState,
    namespaced: true,
    mutations: {
        [SET_LAST_ACTIVE_TICKET_ID]: StoreUtil.createSimpleMutator('lastActiveTicketId'),
        [SET_LAST_FINISHED_TICKET_ID]: StoreUtil.createSimpleMutator('lastFinishedTicketId'),
        [SET_LAST_FETCHED_ACTIVE_TICKET_ID]: StoreUtil.createSimpleMutator('lastFetchedActiveTicketId'),
        [SET_LAST_FETCHED_FINISHED_TICKET_ID]: StoreUtil.createSimpleMutator('lastFetchedFinishedTicketId'),
        [SET_TICKETS_BEINGS_LOADED]: StoreUtil.createSimpleMutator('ticketsBeingLoaded'),
        [CLEAR_ALL](state: ITicketPageState) {
            Object.keys(defaultState).forEach((key, val) => {
                (state as any)[key] = val;
            });
        }
    },
    actions: {
        getFirstPage(context: actionContext, status: TicketsLoadStatus) {
            // loads one more ticket to see if there are more
            return context.dispatch('_getTickets', { status, count: PAGE_SIZE + 1 });
        },
        getNextPage(context: actionContext, status: TicketsLoadStatus) {
            if (status === TicketsLoadStatus.active && context.state.lastActiveTicketId
                || status === TicketsLoadStatus.finished && context.state.lastFinishedTicketId) {
                context.dispatch('_getTickets', { status });
            }
            return context.dispatch('getFirstPage', { status });
        },
        // "private"
        async _getTickets(
            context: actionContext,
            { status, count = PAGE_SIZE }: { status: TicketsLoadStatus, count?: number }) {
            const isActive = status === TicketsLoadStatus.active;
            const lastId = status === TicketsLoadStatus.active
                ? context.state.lastFetchedActiveTicketId : context.state.lastFetchedFinishedTicketId;
            context.commit(SET_TICKETS_BEINGS_LOADED, status);
            const tickets = await context.dispatch(
                'data/tickets/loadTickets',
                {
                    status,
                    lastId,
                    count,
                },
                {
                    root: true,
                }
            );
            context.commit(SET_TICKETS_BEINGS_LOADED, null);
            if (tickets.length) {
                const mutationLastTicketId = status === TicketsLoadStatus.active
                    ? SET_LAST_ACTIVE_TICKET_ID
                    : SET_LAST_FINISHED_TICKET_ID;
                const mutationLastFetchedTicketId = isActive
                    ? SET_LAST_FETCHED_ACTIVE_TICKET_ID
                    : SET_LAST_FETCHED_FINISHED_TICKET_ID;
                let lastId;

                if (tickets.length === count) {
                    lastId = tickets[tickets.length - 2].id;
                    const lastFetchedId = tickets[tickets.length - 1].id;
                    // used last ticket as a hasMore flag
                    // there are always pages * count + 1 tickets loaded
                    context.commit(mutationLastFetchedTicketId, lastFetchedId);
                } else {
                    lastId = tickets[tickets.length - 1].id;
                    // used last ticket as a hasMore flag
                    context.commit(mutationLastFetchedTicketId, null);
                }
                // sets last ticket id which is displayed
                context.commit(mutationLastTicketId, lastId);
            }
            return tickets;
        },
        clearAll: StoreUtil.createSimpleMutatorAction(CLEAR_ALL),
    },
    getters: {
        finishedTickets(state: ITicketPageState, getters: any, rootState: IState, rootGetters: any) {
            const lastTicketId = state.lastFinishedTicketId;
            if (lastTicketId) {
                const finishedTickets = rootGetters['data/tickets/resolvedTickets'];
                const lastTicketIndex = finishedTickets.findIndex((ticket: ITicket) => ticket.id === lastTicketId);
                return finishedTickets.slice(0, lastTicketIndex + 1);
            }
            return [];
        },
        activeTickets(state: ITicketPageState, getters: any, rootState: IState, rootGetters: any) {
            const lastTicketId = state.lastActiveTicketId;
            if (lastTicketId) {
                const active = rootGetters['data/tickets/activeTickets'];
                const lastTicketIndex = active.findIndex((ticket: ITicket) => ticket.id === lastTicketId);
                return active.slice(0, lastTicketIndex + 1);
            }
            return [];
        },
        hasMoreActive: (state: ITicketPageState) =>
            !!StoreUtil.createSimpleGetter('lastFetchedActiveTicketId')(state),
        hasMoreFinished: (state: ITicketPageState) =>
            !!StoreUtil.createSimpleGetter('lastFetchedFinishedTicketId')(state),
        isLoadingActiveTickets: (state: ITicketPageState) => state.ticketsBeingLoaded === TicketsLoadStatus.active,
        isLoadingFinishedTickets: (state: ITicketPageState) => state.ticketsBeingLoaded === TicketsLoadStatus.finished,
    }
};



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--12!./src/app/store/modules/ui/tickets/index.ts